import React, { useEffect, useState, useMemo } from 'react';
import { Copy, Check, RefreshCw, Download, ExternalLink } from 'lucide-react';
import s from './adminStyles';
import CATEGORIES, { CATEGORY_BY_ID } from '../../config/blogCategories';

const API_URL = process.env.REACT_APP_BACKEND_URL;
/* Production base — what gets copied. Editors expect canonical URLs that work
   when pasted into newsletters, social posts, partner emails, etc. */
const PUBLIC_BASE = 'https://casaothers.nl';

/**
 * Blog Export panel — read-only directory of every published blog post.
 * Refetches on tab mount so the list reflects whatever was published seconds
 * ago. Authorisation is enforced at the page level (only mounted inside Admin
 * which itself is gated by useAuth).
 */
const BlogExportPanel = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedAll, setCopiedAll] = useState(false);
  const [copiedRow, setCopiedRow] = useState(null);
  const [filterCat, setFilterCat] = useState('all');
  const [query, setQuery] = useState('');

  const load = () => {
    setLoading(true);
    fetch(`${API_URL}/api/blog?limit=500`)
      .then(r => r.json())
      .then(data => { setPosts(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return posts.filter(p => {
      if (filterCat !== 'all' && (p.category || '') !== filterCat) return false;
      if (q) {
        const hay = `${p.title} ${p.slug} ${(p.tags || []).join(' ')}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [posts, filterCat, query]);

  const formatDate = (iso) => iso
    ? new Date(iso).toLocaleDateString('nl-NL', { year: 'numeric', month: '2-digit', day: '2-digit' })
    : '';

  const buildExport = (list) =>
    list.map(p => `${p.title}\n${PUBLIC_BASE}/blog/${p.slug}`).join('\n\n');

  const copyAll = async () => {
    if (filtered.length === 0) return;
    try {
      await navigator.clipboard.writeText(buildExport(filtered));
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2500);
    } catch { /* clipboard blocked */ }
  };

  const copyRow = async (p) => {
    try {
      await navigator.clipboard.writeText(`${p.title}\n${PUBLIC_BASE}/blog/${p.slug}`);
      setCopiedRow(p.id);
      setTimeout(() => setCopiedRow(null), 1800);
    } catch { /* clipboard blocked */ }
  };

  const downloadTxt = () => {
    const blob = new Blob([buildExport(filtered)], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 10);
    a.download = `casaothers-blog-export-${stamp}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={st.wrapper} data-testid="blog-export-panel">
      <div style={st.headerRow}>
        <div>
          <h2 style={st.title}>Blog Export</h2>
          <p style={st.subtitle}>
            {filtered.length} {filtered.length === 1 ? 'artikel' : 'artikelen'}
            {filterCat !== 'all' && ` · ${CATEGORY_BY_ID[filterCat]?.label || filterCat}`}
            {query && ` · zoekterm "${query}"`}
          </p>
        </div>
        <div style={st.actionsRow}>
          <button type="button" onClick={load} style={st.iconBtn} title="Vernieuwen" data-testid="blog-export-refresh">
            <RefreshCw size={14} />
          </button>
          <button type="button" onClick={downloadTxt} style={st.iconBtn} title="Download .txt" data-testid="blog-export-download">
            <Download size={14} />
          </button>
          <button
            type="button"
            onClick={copyAll}
            disabled={filtered.length === 0}
            style={filtered.length === 0 ? st.copyAllDisabled : st.copyAll}
            data-testid="blog-export-copy-all"
          >
            {copiedAll ? <><Check size={15} /> Gekopieerd!</> : <><Copy size={15} /> Kopieer alles</>}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={st.filterRow}>
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Zoek op titel, slug of tag..."
          style={st.searchInput}
          data-testid="blog-export-search"
        />
        <select
          value={filterCat}
          onChange={e => setFilterCat(e.target.value)}
          style={st.catSelect}
          data-testid="blog-export-cat-filter"
        >
          <option value="all">Alle categorieën</option>
          {CATEGORIES.map(c => (
            <option key={c.id} value={c.id}>{c.label}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div style={st.tableScroll}>
        <table style={st.table} data-testid="blog-export-table">
          <thead>
            <tr>
              <th style={st.th}>Titel</th>
              <th style={st.th}>Slug</th>
              <th style={st.th}>Volledige URL</th>
              <th style={st.th}>Categorie</th>
              <th style={st.th}>Tags</th>
              <th style={st.th}>Publicatiedatum</th>
              <th style={st.thRight}>Actie</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={st.empty}>Laden...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} style={st.empty}>Geen artikelen gevonden.</td></tr>
            ) : (
              filtered.map(p => {
                const fullUrl = `${PUBLIC_BASE}/blog/${p.slug}`;
                const catLabel = p.category ? (CATEGORY_BY_ID[p.category]?.label || p.category) : '—';
                return (
                  <tr key={p.id} style={st.tr} data-testid={`blog-export-row-${p.slug}`}>
                    <td style={st.td}><span style={st.titleCell}>{p.title}</span></td>
                    <td style={st.tdMono}>{p.slug}</td>
                    <td style={st.tdMono}>
                      <a href={fullUrl} target="_blank" rel="noopener noreferrer" style={st.urlLink}>
                        {fullUrl} <ExternalLink size={11} />
                      </a>
                    </td>
                    <td style={st.td}><span style={st.catPill}>{catLabel}</span></td>
                    <td style={st.td}>
                      {(p.tags || []).length === 0 ? <span style={st.muted}>—</span> :
                        (p.tags || []).map(t => <span key={t} style={st.tagChip}>{t}</span>)}
                    </td>
                    <td style={st.td}>{formatDate(p.published_at)}</td>
                    <td style={st.tdRight}>
                      <button
                        type="button"
                        onClick={() => copyRow(p)}
                        style={st.rowCopyBtn}
                        title="Kopieer titel + URL"
                        data-testid={`blog-export-copy-row-${p.slug}`}
                      >
                        {copiedRow === p.id ? <Check size={13} color="#22C55E" /> : <Copy size={13} />}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Live preview of the exact text that "Kopieer alles" will copy.
          Helps editors verify formatting before pasting elsewhere. */}
      {filtered.length > 0 && (
        <details style={st.previewBox}>
          <summary style={st.previewSummary}>Voorbeeld van de export-tekst</summary>
          <pre style={st.previewPre}>{buildExport(filtered)}</pre>
        </details>
      )}
    </div>
  );
};

const st = {
  wrapper: { padding: '0.25rem 0' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' },
  title: { fontFamily: "'Playfair Display', serif", fontSize: '1.5rem', color: '#1A1918', margin: 0, fontWeight: 500 },
  subtitle: { color: '#8C8B88', fontSize: '0.85rem', marginTop: '0.25rem' },
  actionsRow: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  iconBtn: { width: '36px', height: '36px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'white', border: '1px solid #EAE5DD', color: '#585755', cursor: 'pointer' },
  copyAll: {
    display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
    backgroundColor: '#1A1918', color: '#C5A059', border: '1px solid #1A1918',
    padding: '0.55rem 1.1rem', fontSize: '0.82rem', fontWeight: 700,
    letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer',
    fontFamily: "'Manrope', sans-serif",
  },
  copyAllDisabled: {
    display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
    backgroundColor: '#EAE5DD', color: '#8C8B88', border: '1px solid #EAE5DD',
    padding: '0.55rem 1.1rem', fontSize: '0.82rem', fontWeight: 700,
    letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'not-allowed',
    fontFamily: "'Manrope', sans-serif",
  },
  filterRow: { display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' },
  searchInput: { flex: '1 1 220px', minWidth: '180px', border: '1px solid #EAE5DD', padding: '0.55rem 0.85rem', fontSize: '0.9rem', fontFamily: "'Manrope', sans-serif", outline: 'none' },
  catSelect: { border: '1px solid #EAE5DD', backgroundColor: 'white', padding: '0.55rem 0.85rem', fontSize: '0.9rem', fontFamily: "'Manrope', sans-serif", cursor: 'pointer', minWidth: '180px' },

  tableScroll: { overflowX: 'auto', border: '1px solid #EAE5DD', backgroundColor: 'white' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '900px' },
  th: { textAlign: 'left', padding: '0.85rem 1rem', backgroundColor: '#FAF6EE', color: '#1A1918', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', borderBottom: '1px solid #EAE5DD' },
  thRight: { textAlign: 'right', padding: '0.85rem 1rem', backgroundColor: '#FAF6EE', color: '#1A1918', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', borderBottom: '1px solid #EAE5DD' },
  tr: { borderBottom: '1px solid #F1ECE4' },
  td: { padding: '0.85rem 1rem', fontSize: '0.85rem', color: '#1A1918', verticalAlign: 'middle' },
  tdMono: { padding: '0.85rem 1rem', fontSize: '0.78rem', color: '#585755', fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", verticalAlign: 'middle' },
  tdRight: { padding: '0.85rem 1rem', textAlign: 'right', verticalAlign: 'middle' },
  titleCell: { fontWeight: 600, color: '#1A1918' },
  urlLink: { color: '#C5A059', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' },
  catPill: { display: 'inline-block', backgroundColor: '#1A1918', color: '#C5A059', padding: '0.2rem 0.55rem', fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.04em' },
  tagChip: { display: 'inline-block', backgroundColor: '#FAF6EE', color: '#585755', padding: '0.15rem 0.45rem', fontSize: '0.72rem', borderRadius: '999px', marginRight: '0.3rem', marginBottom: '0.2rem' },
  muted: { color: '#8C8B88' },
  empty: { padding: '2rem 1rem', textAlign: 'center', color: '#8C8B88' },
  rowCopyBtn: { width: '30px', height: '30px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'white', border: '1px solid #EAE5DD', color: '#585755', cursor: 'pointer' },

  previewBox: { marginTop: '1rem', backgroundColor: '#FAF6EE', border: '1px solid #EAE5DD', padding: '0.5rem 1rem' },
  previewSummary: { cursor: 'pointer', color: '#1A1918', fontSize: '0.82rem', fontWeight: 600, padding: '0.4rem 0' },
  previewPre: { whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.8rem', color: '#1A1918', backgroundColor: 'white', border: '1px solid #EAE5DD', padding: '0.85rem', maxHeight: '300px', overflowY: 'auto', margin: '0.5rem 0', fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" },
};

// Re-export the unused 's' import sentinel to avoid lint warnings while keeping
// future style customization easy.
void s;

export default BlogExportPanel;
